import appConfig from '../app-config';
import { logInfo, logError } from '../utils/utils';
import * as AmazonCognitoIdentity from 'amazon-cognito-identity-js';

const userPool = new AmazonCognitoIdentity.CognitoUserPool({ UserPoolId: appConfig.cognito.userPoolId, ClientId: appConfig.cognito.clientId });

export const isLoggedIn = () => {
  // If CognitoUser is null, then the user is not longer logged in
  return userPool.getCurrentUser() ? true : false;
}

export const cognitoRefreshSession = async (cognitoUser, refreshToken) => {
  return new Promise((resolve) => {
    cognitoUser.refreshSession(refreshToken, (err, session) => {
      if (err)
        logError(err);

      resolve(session && session.isValid() ? session.idToken.jwtToken : null);
    });
  });
}

export const cognitoGetJwtToken = async () => {
  return new Promise((resolve) => {
    const cognitoUser = userPool.getCurrentUser();
    if (!cognitoUser) {
      logError('CognitoUser is null');
      return resolve(null);
    }

    cognitoUser.getSession(function(err, session) {
      if (err)
        logError(err);

      resolve(session && session.isValid() ? session.idToken.jwtToken : null);
    });
  });
};

export const getAuthenticatedCognitoUser = async () => {
  return new Promise((resolve) => {
    const cognitoUser = userPool.getCurrentUser();
    if (!cognitoUser) {
      logError('CognitoUser is null');
      return resolve(null);
    }

    // Getting the session will create the 'cognitoUser.signInUserSession' object 
    cognitoUser.getSession(function(err) {
      if (err)
        logError(err);
      
      resolve(cognitoUser);
    });
  });
};

export const cognitoAuthenticateUser = async (email, password, rememberMe) => {
  return new Promise((resolve, reject) => {
    const authenticationDetails = new AmazonCognitoIdentity.AuthenticationDetails({ Username : email, Password : password });
    const cognitoUser = new AmazonCognitoIdentity.CognitoUser({ Username : email, Pool : userPool });
  
    cognitoUser.authenticateUser(authenticationDetails, {
      onSuccess: (session) => {
  
        if (rememberMe)
          cognitoRememberedDevice(cognitoUser);

        resolve('Successfully logged in');
      },
      onFailure: (err) => {
        logError(err);
        reject(err.message);
      }
    });
  });
}

export const cognitoRememberedDevice = (cognitoUser) => {
  cognitoUser.setDeviceStatusRemembered({
    onSuccess: (result) => { },
    onFailure: (err) => {
      logError(err);
    }
  });
};

export const cognitoSignUp = async (firstName, lastName, email, password, terms, privacy) => {
  return new Promise((resolve, reject) => {
    const givenNameAttribute = new AmazonCognitoIdentity.CognitoUserAttribute({ Name: 'given_name', Value: firstName });
    const familyNameAttribute = new AmazonCognitoIdentity.CognitoUserAttribute({ Name: 'family_name', Value: lastName });
    const emailAttribute = new AmazonCognitoIdentity.CognitoUserAttribute({ Name: 'email', Value: email });
    const termsAttribute = new AmazonCognitoIdentity.CognitoUserAttribute({ Name: 'custom:terms-and-conditions', Value: terms });
    const privacyAttribute = new AmazonCognitoIdentity.CognitoUserAttribute({ Name: 'custom:privacy-policy', Value: privacy });

    userPool.signUp(email, password, [ givenNameAttribute, familyNameAttribute, emailAttribute, termsAttribute, privacyAttribute ], null, (err) => {
      if (err) {
        logError(err);
        return reject(err.message);
      }

      resolve('Account successfully created! Check your email to validate the account');
    });
  });
}

export const cognitoDeleteUser = async () => {
  const cognitoUser = await getAuthenticatedCognitoUser();
  
  return new Promise((resolve, reject) => {
    if (!cognitoUser)
      return reject('User is not authenticated');
    
    cognitoUser.deleteUser((err, data) => {
      if (err) {
        logError(err);
        return reject(err.message);
      }

      logInfo(data);
      resolve('Account successfully delete!');
    });
  });
}

export const cognitoConfirmPassword = async (email, validationCode, password) => {
  return new Promise((resolve, reject) => {
    const cognitoUser = new AmazonCognitoIdentity.CognitoUser({ Username : email, Pool : userPool });
    
    cognitoUser.confirmPassword(validationCode, password, {
      onSuccess: () => {
        resolve('Password successfully reset');
      },
      onFailure: (err) => {
        logError(err);
        reject(err.message);
      }
    });
  });
}

export const cognitoForgotPassword = async (email) => {
  return new Promise((resolve, reject) => {
    const cognitoUser = new AmazonCognitoIdentity.CognitoUser({ Username : email, Pool : userPool });
    
    cognitoUser.forgotPassword({
      onSuccess: () => { },
      onFailure: err => {
        logError(err);
        reject(err.message);
      },
      inputVerificationCode: (data) => {
        resolve(`Verification code successfully sent to ${data.CodeDeliveryDetails.AttributeName} '${data.CodeDeliveryDetails.Destination}'`);
      }
    });
  });
}

export const changePassword = async (oldPassword, newPassword) => {
  
  const cognitoUser = await getAuthenticatedCognitoUser();
  
  return new Promise((resolve, reject) => {
    
    if (!cognitoUser)
      return reject('User is not authenticated');
    
    cognitoUser.changePassword(oldPassword, newPassword, function(err, result) {
      if (err) {
        logError(err);
        return reject(err.message);
      }
      
      resolve('Password successfully changed');
    });
  });
}

export const cognitoSignOut = () => {
  logInfo('Signing out');
  const cognitoUser = userPool.getCurrentUser();
  if (cognitoUser)
    cognitoUser.signOut();
}